import { useEffect, useState, useMemo } from "react";
import { Chart } from "react-google-charts";
import { ButtonGroup, Button } from "@mui/material";
import axiosFetch from "../utils/axiosFetch";
import Skeleton from "@mui/material/Skeleton";
const optionsChart = {
  title: "BACKEND TRANSACTIONS",
  curveType: "function",
  legend: { position: "up" },
};

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const BackendChart = ({ type, endPointURL, selectedDate }) => {
  const [chartType, setChartType] = useState("hourly");
  const [chartRes, setChartRes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const chartURL = useMemo(() => {
    if (chartType === "hourly") {
      return endPointURL;
    } else {
      return `${type}/${chartType}`;
    }
  }, [chartType, endPointURL, type]);

  useEffect(() => {
    if (chartURL && chartType) {
      setIsLoading(true);
      const { token } = window.localStorage;
      axiosFetch
        .get(chartURL, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          setChartRes([...res.data]);
          setIsLoading(false);
        });
    }
  }, [chartURL, chartType, selectedDate]);

  const getXAxis = () => {
    if (chartType === "monthlyData") return "Month";
    else if (chartType === "quarterlyData") return "Quarter";
    else return "Week";
  };

  const memoizedDataChart = useMemo(() => {
    if (chartType === "hourly") {
      const data = [];
      const transactions = chartRes.filter(
        (i) => i.status_label === "Transactions"
      );
      const heading = transactions.map((i) => i.event_label).sort();
      data.push(["Hourly", ...heading]);
      const hours = [
        "0",
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "10",
        "11",
        "12",
        "13",
        "14",
        "15",
        "16",
        "17",
        "18",
        "19",
        "20",
        "21",
        "22",
        "23",
      ];
      const dataKey = [
        "count_00",
        "count_01",
        "count_02",
        "count_03",
        "count_04",
        "count_05",
        "count_06",
        "count_07",
        "count_08",
        "count_09",
        "count_10",
        "count_11",
        "count_12",
        "count_13",
        "count_14",
        "count_15",
        "count_16",
        "count_17",
        "count_18",
        "count_19",
        "count_20",
        "count_21",
        "count_22",
        "count_23",
      ];
      hours.forEach((hour, index) => {
        const servicesTransCount = transactions.map((t) => t[dataKey[index]]);
        data.push([hour, ...servicesTransCount]);
      });
      return data;
    } else {
      const data = [];
      const transactions = chartRes.filter(
        (i) => i.status_label === "Transactions"
      );
      const xAxis = getXAxis();
      const isMonth = xAxis === "Month";
      const isQuarter = xAxis === "Quarter";
      const headers = new Set(transactions.map((t) => t.event_label).sort());
      const xAxisLabels = transactions.map((t) =>
        isMonth ? t.monthYear.split(" ")[0] : isQuarter ? t.quarter : t.week
      );
      const labels = [...new Set(xAxisLabels)];
      data.push([xAxis, ...headers]);
      const sortedLabels = isMonth
        ? labels
            .map((l) => MONTHS.indexOf(l))
            .sort((a, b) => a - b)
            .map((s) => MONTHS[s])
        : labels.sort((a, b) => a - b);
      sortedLabels.forEach((l) => {
        const lTrans = transactions.filter((t) =>
          isMonth
            ? t.monthYear.split(" ")[0] === l
            : isQuarter
            ? t.quarter === l
            : t.week === l
        );
        const services = [];
        headers.forEach((h) => {
          const countObj = lTrans.find((lt) => lt.event_label === h);
          const count = countObj ? countObj.count_sum : 0;
          services.push(count);
        });
        data.push([
          isMonth ? l : isQuarter ? `Quarter - ${l}` : `Week - ${l}`,
          ...services,
        ]);
      });
      return data;
    }
  }, [chartRes]);

  const handleClick = (selectedType) => {
    setChartType(selectedType);
  };

  return (
    <>
      <div>
        <ButtonGroup
          style={{ position: "absolute", right: "0", marginRight: "100px" }}
        >
          <Button
            variant={chartType === "hourly" ? "contained" : "outlined"}
            onClick={() => handleClick("hourly")}
          >
            Hourly
          </Button>
          <Button
            variant={chartType === "weeklyData" ? "contained" : "outlined"}
            onClick={() => handleClick("weeklyData")}
          >
            Weekly
          </Button>
          <Button
            variant={chartType === "monthlyData" ? "contained" : "outlined"}
            onClick={() => handleClick("monthlyData")}
          >
            Monthly
          </Button>
          <Button
            variant={chartType === "quarterlyData" ? "contained" : "outlined"}
            onClick={() => handleClick("quarterlyData")}
          >
            Quarterly
          </Button>
        </ButtonGroup>
      </div>
      {isLoading ? (
        <>
          <div
            style={{
              align: "center",
              position: "static",
              marginTop: "80px",
            }}
          >
            <Skeleton animation="wave" />
            <Skeleton animation="wave" />
            <Skeleton animation="wave" />
          </div>
        </>
      ) : (
        <>
          <div style={{ marginTop: "100px" }}>
            <Chart
              chartType={chartType === "hourly" ? "Line" : "Bar"}
              width="100%"
              height="400px"
              data={memoizedDataChart}
              options={optionsChart}
            />
          </div>{" "}
        </>
      )}
    </>
  );
};

export default BackendChart;
